import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import '../styles/Header.css';

// На mobile (iOS/Android) НЕ показваме нищо свързано с покупка/абонамент —
// продажбата на PRO е само през web (prednex.com). Това е за App Store /
// Play Store съответствие (Apple Guideline 3.1.1 / Google Play Billing).


function Header({ theme, toggleTheme, onShowPricing, onGoHome }) {
  const { user, signOut, isPro, predictionsLeft, deleteAccount } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const handleDeleteAccount = async () => {
    const msg = lang === 'bg'
      ? 'Сигурен ли си? Това ще изтрие акаунта и всичките ти данни завинаги. Действието е необратимо.'
      : 'Are you sure? This will permanently delete your account and all your data. This cannot be undone.';
    if (!window.confirm(msg)) return;
    const result = await deleteAccount();
    if (!result.ok) {
      alert(lang === 'bg' ? 'Грешка при изтриване. Опитай отново.' : 'Deletion failed. Please try again.');
    }
    // При успех AuthContext вече излезе от профила — UI се връща на началния екран автоматично
  };

  // Detect scroll for header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (hamburgerRef.current && hamburgerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    if (menuOpen) {
      document.addEventListener('touchend', handleClickOutside, true);
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('touchend', handleClickOutside, true);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [menuOpen]);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // User initials for avatar
  const userInitial = user?.email ? user.email[0].toUpperCase() : '?';

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="header-inner">
        {/* Left: Logo */}
        <div className="header-logo" onClick={() => { onGoHome && onGoHome(); setMenuOpen(false); }}>
          <img src="/PREDNEXLOGO2.svg" alt="Prednex" width="32" height="32" />
        </div>

        {/* Center: Navigation (desktop) */}
        <nav className="header-nav desktop-nav">
          <button className="nav-link active" onClick={() => { onGoHome && onGoHome(); setMenuOpen(false); }}>
            {t('home')}
          </button>
          <button
            className="nav-link"
            onClick={() => { onShowPricing(); setMenuOpen(false); }}
          >
            {t('pricing')}
          </button>
        </nav>

        {/* Right: User area (desktop) */}
        <div className="header-right desktop-right">
          <span
            className={`header-badge ${isPro ? 'pro' : 'free'}`}
            onClick={onShowPricing}
            title={isPro ? 'Pro' : 'Free'}
          >
            {isPro ? '⭐ PRO' : 'FREE'}
          </span>

          {!isPro && (
            <span className="header-predictions">
              {predictionsLeft}/3
            </span>
          )}

          <button
            className="header-lang-btn"
            onClick={toggleLang}
            title={t('language')}
          >
            {lang === 'bg' ? 'EN' : 'BG'}
          </button>

          <button
            className="header-theme-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? t('lightTheme') : t('darkTheme')}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          <button className="header-signout" onClick={signOut}>
            {t('logout')}
          </button>
        </div>

        {/* Mobile: hamburger */}
        <div className="header-mobile-controls">
          <span
            className={`header-badge ${isPro ? 'pro' : 'free'}`}
            onClick={onShowPricing}
          >
            {isPro ? '⭐ PRO' : 'FREE'}
          </span>
          <button
            ref={hamburgerRef}
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(prev => !prev); }}
            aria-label="Menu"
            type="button"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`mobile-menu ${menuOpen ? 'show' : ''}`} ref={menuRef}>
        <nav className="mobile-nav">
          <button className="mobile-nav-link" onClick={() => { onGoHome && onGoHome(); setMenuOpen(false); }}>
            🏠 {t('home')}
          </button>
          <button
            className="mobile-nav-link"
            onClick={() => { onShowPricing(); setMenuOpen(false); }}
          >
            💎 {t('pricing')}
          </button>
        </nav>

        <div className="mobile-user-section">
          <div className="mobile-user-row">
            <div className="mobile-user-avatar">{userInitial}</div>
            <span className="mobile-email">{user?.email}</span>
          </div>

          {!isPro && (
            <span className="mobile-predictions">
              {predictionsLeft}/3 {t('predictionsToday')}
            </span>
          )}

          <div className="mobile-actions">
            <button className="mobile-lang-btn" onClick={toggleLang}>
              {lang === 'bg' ? '🇬🇧 EN' : '🇧🇬 BG'}
            </button>
            <button className="mobile-theme-btn" onClick={() => { toggleTheme(); }}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
          
          <button className="mobile-signout" onClick={signOut}>
            {t('logout')}
          </button>
          <button
            className="mobile-signout"
            onClick={handleDeleteAccount}
            style={{ marginTop: '8px', color: '#e53935', borderColor: 'rgba(229,57,53,0.4)' }}
          >
            {lang === 'bg' ? '🗑 Изтрий акаунта' : '🗑 Delete account'}
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;